import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, X, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Dashboard empty-state CTA — shown only when this tenant has zero
 * rows across the 4 seedable domains (menu_items / customers /
 * suppliers / employees). One click seeds the user's chosen domain
 * so they can explore the product immediately.
 *
 * Self-hiding: renders null while loading AND when any domain
 * reports > 0 rows (real or sample). Also permanently dismissible
 * via localStorage key `dash-seed-cta-dismissed`.
 */
export default function DashboardSampleDataCTA() {
  const navigate = useNavigate();
  const [domains, setDomains] = useState(null); // null = loading, [] = ready
  const [seeding, setSeeding] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('dash-seed-cta-dismissed') === '1');

  const load = useCallback(async () => {
    try {
      // Count real rows (not just samples) per domain. Cheapest
      // signal: the admin sample-data listing reports *sample* count
      // but we need the *total* count to decide "is this tenant
      // empty?". We therefore call the domain endpoints directly in
      // parallel and take the total length of each response.
      const [mi, cu, su, em] = await Promise.all([
        api.get('/cashier/menu-all').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/suppliers').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
      ]);
      setDomains([
        { key: 'menu_items', label: 'Menu Items', total: (mi.data || []).length, cta: 'Try the POS', to: '/cashier-pos', hint: 'Seeds 2 menu items (shawarma + coffee)' },
        { key: 'customers', label: 'Customers', total: (cu.data || []).length, cta: 'Open CRM', to: '/customers', hint: 'Seeds 2 demo customers' },
        { key: 'suppliers', label: 'Suppliers', total: (su.data || []).length, cta: 'Open Suppliers', to: '/suppliers', hint: 'Seeds 2 sample suppliers' },
        { key: 'employees', label: 'Employees', total: (em.data || []).length, cta: 'Open HR', to: '/employees', hint: 'Seeds 2 test employees' },
      ]);
    } catch {
      setDomains([]); // fail silent — dashboard shouldn't break if this CTA errors
    }
  }, []);

  useEffect(() => { if (!dismissed) load(); }, [dismissed, load]);

  const dismiss = () => {
    localStorage.setItem('dash-seed-cta-dismissed', '1');
    setDismissed(true);
  };

  const seed = async (key, to) => {
    setSeeding(key);
    try {
      const { data } = await api.post(`/admin/sample-data/${key}/seed`);
      if (data.inserted > 0) {
        toast.success(`Seeded ${data.inserted} sample rows — redirecting…`);
      } else {
        toast.info('Samples were already present — opening the module.');
      }
      // Small delay so the user sees the toast before navigation.
      setTimeout(() => navigate(to), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(null);
    }
  };

  if (dismissed) return null;
  if (domains === null) return null;
  // Hide once ANY domain has any data — user is no longer "empty".
  // Allow `?force-cta=1` debug flag for QA + Playwright force-render.
  const force = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('force-cta') === '1';
  const isEmpty = force || (domains.length > 0 && domains.every((d) => d.total === 0));
  if (!isEmpty) return null;

  return (
    <Card
      className="relative p-6 bg-gradient-to-br from-violet-50 via-white to-sky-50 border-violet-200/70 shadow-sm"
      data-testid="dashboard-seed-cta"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground rounded-full p-1"
        data-testid="dashboard-seed-cta-dismiss"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center shrink-0">
          <Sparkles size={22} />
        </div>
        <div className="min-w-0">
          <h3 className="font-outfit text-lg font-semibold text-foreground">
            New here? Seed demo data to explore instantly
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Your tenant looks empty. Pick a module below — we'll drop in a
            couple of sample rows (tagged, never mixed with real data) so
            you can take the feature for a spin. Clear anytime from
            Settings → Import Data.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {domains.map((d) => (
          <div
            key={d.key}
            className="bg-white rounded-xl border border-border p-4 flex flex-col gap-3 hover:shadow-md transition-shadow"
            data-testid={`dashboard-seed-${d.key}`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold">{d.label}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Empty</span>
            </div>
            {d.hint && (
              <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`dashboard-seed-hint-${d.key}`}>
                {d.hint}
              </p>
            )}
            <div className="flex gap-2 mt-auto">
              <Button
                size="sm"
                className="flex-1 rounded-lg text-xs bg-violet-600 hover:bg-violet-700 text-white"
                disabled={seeding === d.key}
                onClick={() => seed(d.key, d.to)}
                data-testid={`dashboard-seed-btn-${d.key}`}
              >
                {seeding === d.key ? (
                  <Loader2 size={13} className="mr-1 animate-spin" />
                ) : (
                  <Sparkles size={13} className="mr-1" />
                )}
                Seed & open
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-lg text-xs text-muted-foreground hover:text-foreground"
                onClick={() => navigate(d.to)}
                data-testid={`dashboard-open-${d.key}`}
              >
                <ArrowRight size={13} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
